/**
 * Usage metrics without our own backend and without Konstantin's weekly export (roadmap P1-10): like the error
 * beacon (report.ts), a GET of a 1×1 image on our nginx — `m.gif` — read from the container access log by
 * scripts/metrics-report.mjs. Only counts go out: an anonymous random id (to count people, not a Telegram id),
 * a random launch id, the event, object and pass type codes, the screen, the launch source, timings and API error
 * codes. Never the token, initData, phone, plates, names or addresses.
 */
export interface MetricsContext {
  /** URL of m.gif next to the app (respects the /dev/ base). */
  endpoint: string;
  version: string;
  platform: string;
  /** Telegram CloudStorage: the anonymous id follows the account across devices. */
  storage: { get(key: string): Promise<string | null>; set(key: string, value: string): Promise<void> } | null;
  /** How the app was opened: the kind of deep link (pass, request, news, object) or none. */
  source?: string;
  /** Milliseconds from navigation start until the app code ran. */
  loadMs?: number;
  send?: (url: string) => void;
  local?: Pick<Storage, 'getItem' | 'setItem'> | null;
  random?: () => string;
  now?: () => number;
}

export type Fields = Record<string, string | number | undefined>;

const ID_KEY = 'mid';
const MAX_PER_SESSION = 80;

let context: MetricsContext | null = null;
let uid: string | null = null;
let launch = '';
let objectId: number | null = null;
let sent = 0;
let form: { screen: string; at: number } | null = null;
const bannersSeen = new Set<number>();
const queue: Array<[string, Fields]> = [];

const randomId = () => Math.random().toString(36).slice(2, 12) + Math.random().toString(36).slice(2, 6);
const clock = () => (context?.now ?? Date.now)();

function safeLocal(ctx: MetricsContext): Pick<Storage, 'getItem' | 'setItem'> | null {
  if (ctx.local !== undefined) return ctx.local;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function emit(code: string, fields: Fields) {
  if (!context || !uid || sent >= MAX_PER_SESSION) return;
  sent += 1;
  const q = new URLSearchParams({ c: code, u: uid, l: launch, v: context.version, p: context.platform });
  if (objectId) q.set('o', String(objectId));
  for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== '') q.set(k, String(v));
  // A unique tail: a cached image never reaches the nginx log.
  q.set('r', Math.random().toString(36).slice(2, 8));
  (context.send ?? defaultSend)(`${context.endpoint}?${q.toString()}`);
}

/**
 * Starts metrics: finds or creates the anonymous id (device first, then the Telegram account), sends «start»
 * with n=1 when the id is new — the first launch of the app by this person —, the launch source and the load
 * time, and flushes queued events.
 */
export async function initMetrics(ctx: MetricsContext): Promise<void> {
  context = ctx;
  launch = (ctx.random ?? randomId)().slice(0, 8);
  const local = safeLocal(ctx);
  let id: string | null = null;
  try {
    id = local?.getItem(ID_KEY) ?? null;
  } catch {
    id = null;
  }
  if (!id && ctx.storage) id = await ctx.storage.get(ID_KEY).catch(() => null);
  const isNew = !id || !/^[a-z0-9]{6,20}$/.test(id);
  if (isNew) id = (ctx.random ?? randomId)();
  try {
    local?.setItem(ID_KEY, id!);
  } catch {
    // Storage blocked: the account copy still keeps the id.
  }
  if (isNew && ctx.storage) void ctx.storage.set(ID_KEY, id!).catch(() => undefined);
  uid = id;
  const lt = ctx.loadMs !== undefined && ctx.loadMs > 0 && ctx.loadMs < 120_000 ? Math.round(ctx.loadMs) : undefined;
  emit('start', { n: isNew ? 1 : undefined, src: ctx.source || 'none', lt });
  for (const [code, fields] of queue.splice(0)) emit(code, fields);
}

/** Sends an event; before initMetrics finished it waits in a short queue. */
export function track(code: string, fields: Fields = {}) {
  if (uid) emit(code, fields);
  else if (queue.length < 20) queue.push([code, fields]);
}

/** The object the resident is in now: added to every following event (pilot by посёлок). */
export function setMetricsObject(id: number | null) {
  objectId = id;
}

/** «/quick/taxi» → «quick-taxi»: the screen an order came from, without ids. */
export function screenOf(pathname: string, base = '/'): string {
  const path = pathname.startsWith(base) ? pathname.slice(base.length - 1) : pathname;
  const parts = path.split('/').filter((p) => p && !/\d/.test(p)).slice(0, 2);
  return parts.join('-').replace(/[^a-z-]/gi, '').slice(0, 24) || 'home';
}

/** Screens where something is ordered: opening one is the first step of the funnel. */
export const FORM_SCREENS = new Set(['new', 'quick-taxi', 'quick-courier', 'crew', 'regular', 'invite', 'requests-new', 'more-join', 'more-confidances']);

/** The resident opened an order form: «form» event, and the clock for the time to order starts. */
export function formOpened(screen: string) {
  if (!FORM_SCREENS.has(screen)) return;
  form = { screen, at: clock() };
  track('form', { s: screen });
}

/** Seconds since the order form of this screen opened, if it is the one open now. */
function formSeconds(screen: string): number | undefined {
  if (!form || form.screen !== screen) return undefined;
  const s = Math.round((clock() - form.at) / 1000);
  return s >= 0 && s < 3600 ? s : undefined;
}

/**
 * A successful create request of mobile-api, the same points v1 tracks by source (TrackTenantApiSource):
 * a pass, an invite, a request (with its type) or a power of attorney.
 */
export function createdEvent(path: string, method: string | undefined, body: unknown): Fields | null {
  if ((method ?? 'GET').toUpperCase() !== 'POST') return null;
  const b = (body ?? {}) as { durationType?: unknown; type?: unknown };
  const clean = path.split('?')[0];
  if (clean === '/v1/passes') return { t: 'pass', d: Number(b.durationType) || undefined };
  if (clean === '/v1/passes/invite') return { t: 'invite', d: Number(b.durationType) || undefined };
  if (clean === '/v1/requests') return { t: 'request', rt: Number(b.type) || undefined };
  if (clean === '/v1/confidances') return { t: 'confidance' };
  return null;
}

/** «create» with the screen and, when the form was opened in this launch, the seconds it took. */
export function trackCreate(event: Fields, screen: string) {
  track('create', { ...event, s: screen, sec: formSeconds(screen) });
}

/** A create request refused by mobile-api: «fail» with the API error code (no message text). */
export function trackFail(event: Fields, screen: string, errorCode: string) {
  track('fail', { ...event, s: screen, e: errorCode.replace(/[^A-Z0-9_]/gi, '').slice(0, 40) || 'UNKNOWN' });
}

/** «https://www.partner.ru/promo?x=1» → «partner.ru»: who the banner advertises, without the path. */
export function linkHost(link: string | null): string | undefined {
  if (!link) return undefined;
  try {
    return new URL(link).host.replace(/^www\./, '').slice(0, 40) || undefined;
  } catch {
    return undefined;
  }
}

/** A partner banner was on screen: «banner_view», once per launch for each banner. */
export function bannerShown(id: number, link: string | null) {
  if (bannersSeen.has(id)) return;
  bannersSeen.add(id);
  track('banner_view', { b: id, bh: linkHost(link) });
}

/** A tap on a partner banner that opens its link: «banner_click». */
export function bannerClicked(id: number, link: string | null) {
  track('banner_click', { b: id, bh: linkHost(link) });
}

function defaultSend(url: string) {
  try {
    new Image().src = url;
  } catch {
    // Metrics must never break the app.
  }
}

/** Tests only. */
export function resetMetrics() {
  context = null;
  uid = null;
  launch = '';
  objectId = null;
  sent = 0;
  form = null;
  queue.length = 0;
  bannersSeen.clear();
}
